import { useTranslation } from 'react-i18next';
import { Check, Clock } from 'lucide-react';
import { PrefSection, type PrefVariant } from '@/components/PrefSection';
import {
  NUTRIENT_TIMING_MAX,
  useNutrientTimingPrefs,
} from '@/hooks/useNutrientTimingPrefs';
import { TIMING_NUTRIENTS } from '@/lib/nutrientDistribution';
import { cn } from '@/lib/utils';

/**
 * Profile-page control for the patient Summary's Nutrient-timing card: pick up
 * to 3 nutrients to chart by time of day. Persists to this browser like the
 * other display preferences. Applies on the next Summary render.
 */
export const NutrientTimingPrefs = ({ variant = 'card' }: { variant?: PrefVariant }) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useNutrientTimingPrefs();

  const toggle = (key: string) => {
    if (selected.includes(key)) {
      setSelected(selected.filter((k) => k !== key));
    } else if (selected.length < NUTRIENT_TIMING_MAX) {
      setSelected([...selected, key]);
    }
  };

  return (
    <PrefSection
      variant={variant}
      icon={Clock}
      title={t('Nutrient timing')}
      description={t('Choose up to 3 nutrients to chart by time of day on the patient Summary. Saved to this browser.')}
      action={
        <span className="text-xs text-muted-foreground">
          {t('{{count}}/{{max}}', { count: selected.length, max: NUTRIENT_TIMING_MAX })}
        </span>
      }
    >
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {TIMING_NUTRIENTS.map((n) => {
          const active = selected.includes(n.key);
          const atMax = !active && selected.length >= NUTRIENT_TIMING_MAX;
          return (
            <button
              key={n.key}
              type="button"
              onClick={() => toggle(n.key)}
              aria-pressed={active}
              disabled={atMax}
              className={cn(
                'flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
                active ? 'border-primary bg-primary/5' : 'border-border bg-surface hover:bg-muted',
                atMax && 'cursor-not-allowed opacity-40 hover:bg-surface',
              )}
            >
              <span aria-hidden className="h-3 w-3 shrink-0 rounded-sm" style={{ backgroundColor: n.colour }} />
              <span className="min-w-0 flex-1 truncate text-left font-medium">{t(n.label)}</span>
              {active && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
            </button>
          );
        })}
      </div>
    </PrefSection>
  );
};
